// shortcutRegistry — declarative metadata for every keyboard shortcut in the
// app (Wave 2C: "Keyboard shortcut help window" + "Shortcuts should be on the
// menu").
//
// METADATA ONLY. The listeners stay where they always lived — App.jsx (F,
// ?/Ctrl+/), DockedShell.jsx (W/E/R, scene undo/redo, ⌥O/⌥I/⌥A), AssetGrid.jsx
// (F2 / Ctrl+A / Delete / Esc), the graph editors (Delete + Ctrl+Z) and
// MaterialInspector.jsx (Ctrl+Z / Ctrl+Y). This module is the single place
// that KNOWS about all of them, so the help window (ShortcutHelp.jsx) and the
// menubar accelerator hints (AppMenuBar.jsx) can never disagree with each
// other. When a listener is added or removed, update the entry here — the
// pinned-coverage unit test (tests/unit/shortcutRegistry.test.mjs) fails
// loudly on a silent deletion.
//
// Leaf module: no Vite-only imports, importable under node --test.
//
// Key token set (locked by the unit test):
//   'Mod'   — the platform primary modifier: ⌘ on mac, Ctrl elsewhere.
//   'Ctrl' / 'Alt' / 'Shift' — literal modifiers (⌃ / ⌥ / ⇧ on mac).
//   anything else — rendered verbatim ('F', 'F2', '/', 'Delete', …).
//
// Entry shape: { id, keys, scope, label, also?, when? }
//   also — alternate combos for the same action (e.g. Backspace for Delete).
//   when — human-readable precondition shown dim in the help window.

/** Scope display order for the help window's groups. */
export const SCOPE_ORDER = [
  'Global',
  'Viewport',
  'Outliner',
  'Inspector',
  'Content browser',
  'Graph editors',
]

export const ALL_SHORTCUTS = [
  // ── Global ─────────────────────────────────────────────────────────────────
  {
    id: 'shortcut-help',
    keys: ['Mod', '/'],
    also: [['?']],
    scope: 'Global',
    label: 'Keyboard shortcuts (this window)',
  },
  {
    id: 'frame-avatar',
    keys: ['F'],
    scope: 'Global',
    label: 'Frame the avatar in the viewport',
  },
  {
    id: 'close-overlay',
    keys: ['Escape'],
    scope: 'Global',
    label: 'Close dialog / menu / overlay',
  },
  {
    // numericScrubInstall.js — the wheel does the same, see numericScrub.js.
    id: 'number-field-step',
    keys: ['ArrowUp'],
    also: [['ArrowDown']],
    scope: 'Global',
    label: 'Step a number field by the digit right of the cursor (or scroll)',
    when: 'editing a number field',
  },

  // ── Viewport (transform gizmo — DockedShell) ───────────────────────────────
  {
    id: 'gizmo-translate',
    keys: ['W'],
    scope: 'Viewport',
    label: 'Gizmo: translate',
    when: 'object selected',
  },
  {
    id: 'gizmo-rotate',
    keys: ['E'],
    scope: 'Viewport',
    label: 'Gizmo: rotate',
    when: 'object selected',
  },
  {
    id: 'gizmo-scale',
    keys: ['R'],
    scope: 'Viewport',
    label: 'Gizmo: scale',
    when: 'object selected',
  },
  {
    id: 'scene-undo',
    keys: ['Mod', 'Z'],
    scope: 'Viewport',
    label: 'Undo transform edit',
  },
  {
    id: 'scene-redo',
    keys: ['Mod', 'Shift', 'Z'],
    also: [['Ctrl', 'Y']],
    scope: 'Viewport',
    label: 'Redo transform edit',
  },

  // ── Outliner ───────────────────────────────────────────────────────────────
  {
    id: 'toggle-outliner',
    keys: ['Alt', 'O'],
    scope: 'Outliner',
    label: 'Toggle outliner panel',
  },

  // ── Inspector ──────────────────────────────────────────────────────────────
  {
    id: 'toggle-inspector',
    keys: ['Alt', 'I'],
    scope: 'Inspector',
    label: 'Toggle inspector panel',
  },
  {
    id: 'toggle-agent',
    keys: ['Alt', 'G'],
    scope: 'Inspector',
    label: 'Toggle agent panel (ENG-1029)',
  },
  {
    id: 'material-undo',
    keys: ['Mod', 'Z'],
    scope: 'Inspector',
    label: 'Undo material edit',
    when: 'material inspector open',
  },
  {
    id: 'material-redo',
    keys: ['Mod', 'Shift', 'Z'],
    also: [['Ctrl', 'Y']],
    scope: 'Inspector',
    label: 'Redo material edit',
    when: 'material inspector open',
  },

  // ── Content browser (assets dock) ──────────────────────────────────────────
  {
    id: 'toggle-assets-dock',
    keys: ['Alt', 'A'],
    scope: 'Content browser',
    label: 'Toggle assets dock',
  },
  {
    id: 'rename-asset',
    keys: ['F2'],
    scope: 'Content browser',
    label: 'Rename selected asset / folder',
  },
  {
    id: 'select-all-assets',
    keys: ['Mod', 'A'],
    scope: 'Content browser',
    label: 'Select all visible assets',
  },
  {
    id: 'delete-assets',
    keys: ['Delete'],
    also: [['Backspace']],
    scope: 'Content browser',
    label: 'Delete selected assets',
    when: 'multi-selection active',
  },
  {
    id: 'clear-asset-selection',
    keys: ['Escape'],
    scope: 'Content browser',
    label: 'Clear multi-selection',
  },

  // ── Graph editors (body / face / events) ───────────────────────────────────
  {
    id: 'graph-delete',
    keys: ['Delete'],
    scope: 'Graph editors',
    label: 'Delete selected nodes / edges',
  },
  {
    id: 'graph-undo',
    keys: ['Mod', 'Z'],
    scope: 'Graph editors',
    label: 'Undo node / edge delete, or a variable / function delete from My Blueprint',
  },
  {
    id: 'graph-copy',
    keys: ['Mod', 'C'],
    scope: 'Graph editors',
    label: 'Copy selected nodes (pasteable into another character)',
    when: 'nodes selected, pointer outside a text field',
  },
  {
    id: 'graph-cut',
    keys: ['Mod', 'X'],
    scope: 'Graph editors',
    label: 'Cut selected nodes',
    when: 'nodes selected, pointer outside a text field',
  },
  {
    id: 'graph-paste',
    keys: ['Mod', 'V'],
    scope: 'Graph editors',
    label: 'Paste nodes at the pointer — refused across the events/animation divide',
    when: 'a graph editor is open',
  },
  {
    id: 'graph-find',
    keys: ['Mod', 'F'],
    scope: 'Graph editors',
    label: 'Find a node — or paste a coordinate to jump to it',
    when: 'a graph editor is open',
  },
  // Arrange (ENG-1069) — Unreal's chords, acting on the multi-selection.
  {
    id: 'graph-align-left',
    keys: ['Shift', 'A'],
    scope: 'Graph editors',
    label: 'Align selected nodes left',
    also: [['Shift', 'D'], ['Shift', 'W'], ['Shift', 'S']],
    when: 'the pointer is over a graph editor',
  },
  {
    id: 'graph-align-center',
    keys: ['Shift', 'Alt', 'S'],
    scope: 'Graph editors',
    label: 'Align selected nodes to their center (⇧⌥W = middle)',
    also: [['Shift', 'Alt', 'W']],
    when: 'the pointer is over a graph editor',
  },
  {
    id: 'graph-straighten',
    keys: ['Q'],
    scope: 'Graph editors',
    label: 'Straighten connections between selected nodes',
    when: 'the pointer is over a graph editor',
  },
]

const BY_ID = new Map(ALL_SHORTCUTS.map(e => [e.id, e]))

/** Look up one entry; throws on an unknown id so a typo fails loudly. */
export function getShortcut(id) {
  const e = BY_ID.get(id)
  if (!e) throw new Error(`unknown shortcut id: ${id}`)
  return e
}

const MAC_GLYPHS = { Mod: '⌘', Ctrl: '⌃', Alt: '⌥', Shift: '⇧' }

/**
 * 'mac' | 'win'. Lazy so the module stays importable (and the formatter pure)
 * under node --test, where `navigator` may not exist or may lack `platform`.
 */
export function detectPlatform() {
  if (typeof navigator !== 'undefined' && /mac/i.test(String(navigator.platform ?? ''))) return 'mac'
  return 'win'
}

/**
 * Platform display string for one combo. Accepts a registry entry or a raw
 * key array. mac: glyphs, no separator (⌥A, ⌘⇧Z). win: words + '+' (Alt+A,
 * Ctrl+Shift+Z).
 */
export function formatKeys(entryOrKeys, platform = detectPlatform()) {
  const keys = Array.isArray(entryOrKeys) ? entryOrKeys : entryOrKeys.keys
  if (platform === 'mac') return keys.map(k => MAC_GLYPHS[k] ?? k).join('')
  return keys.map(k => (k === 'Mod' ? 'Ctrl' : k)).join('+')
}

/** Primary combo plus alternates, joined for the help window's key column. */
export function formatKeyList(entry, platform = detectPlatform()) {
  const combos = [entry.keys, ...(entry.also ?? [])]
  return combos.map(c => formatKeys(c, platform)).join(' / ')
}
